// ✅ client/src/pages/UserHome.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {socket, apiFetch, apiJson, refreshSessionUser} from '../lib/api';
import '../App.css';
import logo from '../assets/chitconnect-logo-blue.png';
import { DateTime } from 'luxon';
import { parseAnyDateTime } from '../lib/dates';
import BidStatusBanner from '../components/BidStatusBanner';
import BiddingPopup from '../components/BiddingPopup';
import Greeting from '../components/Greeting';
import OrganiserCreateCard from '../components/OrganiserCreateCard';

// 🔔 Push helpers
import { registerPush, unregisterPush, isPushSupported } from '../lib/push';

// Normalise schedule API responses: some endpoints return an array, others return { schedule: [...] } etc.
function normalizeScheduleRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.schedule)) return payload.schedule;
  if (payload && Array.isArray(payload.rows)) return payload.rows;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

// Toggle for page-level debug logs
const DEBUG_BIDDING = false;

export default function UserHome({ user }) {
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [users, setUsers] = useState([]);

// ---- derive organiser / create-group capability from user ----
const lvl = String(user?.access_level || user?.role || '').trim().toLowerCase();
const isSuperadmin = lvl === 'superadmin';

// can_create_group might be:
//  - boolean (true/false)
//  - OR an object like { allowed: true/false, reason: '...' }
const rawCan = user?.canCreateGroup ?? user?.can_create_group;

const allowedFromApi =
  rawCan && typeof rawCan === 'object'
    ? !!rawCan.allowed
    : !!rawCan;

// Final decision: only superadmin OR explicit allowed flag
const canCreateGroupComputed = isSuperadmin || allowedFromApi;

const effectiveUser = {
  ...user,
  canCreateGroup: canCreateGroupComputed,
  can_create_group: canCreateGroupComputed,
};


  // /api/bidding/current/:gid payload (progress/period flags etc.)
  const [bidInfo, setBidInfo] = useState({});      // { [gid]: currentPayload }

  // /api/bidding/status/:gid payload
  const [bidStatusMap, setBidStatusMap] = useState({}); // { [gid]: statusPayload }

  // Window times used for timers/banners/auto-open
  // { [gid]: { start_time, end_time, server_time? } }
  const [biddingTimes, setBiddingTimes] = useState({});

  // Next upcoming auction start per group (computed from schedule)
  const [nextScheduleStarts, setNextScheduleStarts] = useState({}); // { [gid]: nextStartMs (number) }

  const [expandedGroupId, setExpandedGroupId] = useState(null);

  const [activeBiddingGroup, setActiveBiddingGroup] = useState(null);
  const [showBiddingPopup, setShowBiddingPopup] = useState(false);
  const [popupShownForGroup, setPopupShownForGroup] = useState(null);

  // 🔹 Action items state (now includes bank-user/bank-link/bank-organiser + push)
  const [actionItems, setActionItems] = useState([]); // [{type, group?, ...}]
  // Show only a few action items by default (mobile-friendly)
  const [actionLimit, setActionLimit] = useState(3);

  // Clamp the visible count when the list changes (e.g., after refresh)
  useEffect(() => {
    setActionLimit((n) => {
      const min = 3;
      const len = Array.isArray(actionItems) ? actionItems.length : 0;
      if (len <= min) return min;
      return Math.min(Math.max(min, n), len);
    });
  }, [actionItems]);

  // Avoid "page refresh" feeling: only update state when data actually changes
  const bidInfoHashRef = useRef('');
  const groupsHashRef = useRef('');
  const timesHashRef = useRef('');
  const statusHashRef = useRef('');

  const stableHash = useCallback((obj) => {
    try {
      if (!obj || typeof obj !== 'object') return '';
      const keys = Object.keys(obj).sort();
      // compact hash; only JSON stringify each value (safe enough for our payloads)
      return keys.map((k) => k + ':' + JSON.stringify(obj[k] ?? null)).join('|');
    } catch {
      return String(Date.now()); // worst-case: force update
    }
  }, []);


  // 🔔 Push subscription state (from /lib/push register result)
  const [pushState, setPushState] = useState(null); // { ok:boolean, reason?:string }

  // 📊 Group progress (finished periods vs total duration)
  const [progressById, setProgressById] = useState({}); // { [groupId]: { finished, total, percent } }

  
  // --------------------------- group ordering ---------------------------
  // Order groups by earliest bidding time:
  //  - If a group has an active auction window, use its current period start_time
  //  - Otherwise fall back to firstStart (from /status) or the group's auction_start_date
  //  - Unknown dates sort last
  const groupSortKeyMs = useCallback((group) => {
    const gid = group?.id ?? group?.group_id;
    const st = gid ? bidStatusMap?.[gid] : null;
    const nowMs = Date.now();

    // If a bidding window is active now (server schedule truth), keep it at the top
    if (st?.start_time && st?.end_time) {
      const stMs = Date.parse(st.start_time);
      const etMs = Date.parse(st.end_time);
      if (Number.isFinite(stMs) && Number.isFinite(etMs) && nowMs >= stMs && nowMs <= etMs) {
        return nowMs - 1000; // active auctions first
      }
    }

    // Next upcoming start computed from the schedule (best signal for "nearest bidding")
    const nextMs = gid ? nextScheduleStarts?.[gid] : null;
    if (Number.isFinite(nextMs)) return nextMs;
    // Fallbacks
    const firstStartIso = st?.firstStart || null;
    if (firstStartIso) {
      const ms = Date.parse(firstStartIso);
      if (Number.isFinite(ms)) return ms;
    }

    const dt = group?.auction_start_date ? parseAnyDateTime(group.auction_start_date, 'utc') : null;
    return dt?.isValid ? dt.toMillis() : Number.POSITIVE_INFINITY;
  }, [bidStatusMap, nextScheduleStarts]);

  const sortedGroups = useMemo(() => {
    if (!Array.isArray(groups) || groups.length === 0) return [];
    return [...groups].sort((a, b) => {
      const ka = groupSortKeyMs(a);
      const kb = groupSortKeyMs(b);
      if (ka !== kb) return ka - kb;
      // stable tie-breakers: name then id
      const na = String(a?.name || '').toLowerCase();
      const nb = String(b?.name || '').toLowerCase();
      if (na !== nb) return na.localeCompare(nb);
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [groups, groupSortKeyMs]);


  // Compute "next bidding start" from the server schedule so groups can be sorted by nearest upcoming bidding.
//
// Performance notes:
// - Schedule fetch is N+1 (per-group). On mobile this can feel slow.
// - We cache next start per group in sessionStorage for a short TTL.
// - We limit concurrency to avoid spamming the network.
  useEffect(() => {
    let cancelled = false;

    const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
    const cacheKey = (gid) => `cc_nextStartMs_v1:${gid}`;

    const readCache = (gid) => {
      try {
        const raw = sessionStorage.getItem(cacheKey(gid));
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || !Number.isFinite(obj.ms) || !Number.isFinite(obj.at)) return null;
        if ((Date.now() - obj.at) > CACHE_TTL_MS) return null;
        return obj.ms;
      } catch {
        return null;
      }
    };

    const writeCache = (gid, ms) => {
      try {
        sessionStorage.setItem(cacheKey(gid), JSON.stringify({ ms, at: Date.now() }));
      } catch {
        // ignore storage failures
      }
    };

    const run = async () => {
      if (!Array.isArray(groups) || groups.length === 0) {
        setNextScheduleStarts({});
        return;
      }

      const nowMs = Date.now();
      const nextMap = {};

      // 1) Apply cached values immediately (fast first paint)
      for (const g of groups) {
        const gid = g?.id ?? g?.group_id;
        if (!gid) continue;
        const cached = readCache(gid);
        if (Number.isFinite(cached)) nextMap[gid] = cached;
      }

      // Show cached map if we have one (don't wait for network)
      const cachedHash = stableHash(nextMap);
      setNextScheduleStarts((prev) => (stableHash(prev) === cachedHash ? prev : nextMap));

      // 2) Fetch missing/expired schedules with limited concurrency
      const needFetch = groups.filter((g) => {
        const gid = g?.id ?? g?.group_id;
        if (!gid) return false;
        const cached = readCache(gid);
        return !Number.isFinite(cached);
      });

      const CONCURRENCY = 4;
      let idx = 0;

      const worker = async () => {
        while (!cancelled) {
          const current = needFetch[idx++];
          if (!current) return;
          const gid = current?.id ?? current?.group_id;
          if (!gid) continue;

          try {
            const r = await apiFetch(`/api/groups/${gid}/schedule`);
            if (!r.ok) continue;
            const payload = await r.json();
            const rows = normalizeScheduleRows(payload);
            if (!Array.isArray(rows) || rows.length === 0) continue;

            // MIN(start_time) that is >= now
            let best = null;
            for (const row of rows) {
              const stIso = row?.start_time || row?.startTime;
              if (!stIso) continue;
              const ms = Date.parse(stIso);
              if (!Number.isFinite(ms)) continue;
              if (ms >= nowMs && (best == null || ms < best)) best = ms;
            }

            if (best != null) {
              nextMap[gid] = best;
              writeCache(gid, best);
            }
          } catch {
            // ignore
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, needFetch.length) }, () => worker()));

      if (cancelled) return;

      // Only set if changed
      const hash = stableHash(nextMap);
      setNextScheduleStarts((prev) => (stableHash(prev) === hash ? prev : nextMap));
    };

    run();
    return () => { cancelled = true; };
  }, [groups, stableHash]);
// --------------------------- utils ---------------------------
  const safeJson = async (res) => { try { return await res.json(); } catch { return null; } };

  // Normalize any plausible object into {start_time, end_time, server_time}
  const selectWindow = (obj) => {
    if (!obj || typeof obj !== 'object') return null;

    const st = obj.start_time || obj.startTime || obj.start || obj.window_start || obj.start_at;
    const et = obj.end_time   || obj.endTime   || obj.end   || obj.window_end   || obj.end_at;
    const sv = obj.server_time || obj.serverTime || null;
    const ac = Object.prototype.hasOwnProperty.call(obj, 'auction_active')
      ? !!obj.auction_active
      : null;
    if (st && et) return { start_time: st, end_time: et, server_time: sv, auction_active: ac };

    if (obj.window && (obj.window.start_time || obj.window.start) && (obj.window.end_time || obj.window.end)) {
      return {
        start_time: obj.window.start_time || obj.window.start,
        end_time:   obj.window.end_time   || obj.window.end,
        server_time: sv,
        auction_active: ac,
      };
    }

    const p = obj.period || obj.current_window || null;
    if (p && (p.start_time || p.start) && (p.end_time || p.end)) {
      return {
        start_time: p.start_time || p.start,
        end_time:   p.end_time   || p.end,
        server_time: sv,
        auction_active: ac,
      };
    }

    const arr = Array.isArray(obj.periods) ? obj.periods : (Array.isArray(obj.windows) ? obj.windows : null);
    if (arr && arr.length) {
      const a0 = arr[0];
      if ((a0.start_time || a0.start) && (a0.end_time || a0.end)) {
        return {
          start_time: a0.start_time || a0.start,
          end_time:   a0.end_time   || a0.end,
          server_time: sv,
        };
      }
    }

    return null;
  };

  // Parse both ISO ("2025-12-30T08:49:00Z") and MySQL DATETIME ("2025-12-30 08:49:00") as UTC.
  const parseUtc = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    let dt = DateTime.fromISO(s, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromSQL(s, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromFormat(s, 'yyyy-LL-dd HH:mm:ss', { zone: 'utc' });
    return dt.isValid ? dt : null;
  };

  function canReviewPaymentsForGroup(me, group) {
    const lvl = String(me?.access_level || me?.role || '').trim().toLowerCase();
    if (lvl === 'superadmin') return true;

    // organiser/owner can review
    const role = String(group?.role || group?.my_role || '').trim().toLowerCase();
    if (role === 'organiser') return true;

    if (group?.is_organiser === 1 || group?.isOrganiser === true) return true;
    if (Number(group?.owner_id) === Number(me?.id)) return true;

    return false;
  }

  // --- Period 1 start helper (use group's own date/time) ---
  const getMyMembershipIndices = useCallback((group) => {
  // We may receive memberships in a few shapes depending on which API populated the group object.
  // Support:
  // - group.my_memberships: [1,2] or ["1","2"] or [{membership_index:1}, ...]
  // - group.members: [{ user_id, membership_index }, ...]
  // Fallback: [1]
  const out = [];

  if (Array.isArray(group?.my_memberships) && group.my_memberships.length) {
    for (const x of group.my_memberships) {
      const n =
        typeof x === 'number' || typeof x === 'string'
          ? Number(x)
          : Number(x?.membership_index ?? x?.membershipIndex ?? x?.index ?? x?.membership ?? x?.slot);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }

  if (out.length === 0 && Array.isArray(group?.members)) {
    for (const m of group.members) {
      if (String(m?.user_id) !== String(user?.id)) continue;
      const n = Number(m?.membership_index ?? m?.membershipIndex ?? 1);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }

  // de-dupe + sort
  const unique = Array.from(new Set(out)).sort((a, b) => a - b);
  return unique.length ? unique : [1];
}, [user?.id]);


// memoized: pure function (no outside state); empty deps are fine
const getPeriod1Start = useCallback((group) => {
  const tz =
    group.auction_timezone ||
    group.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const date =
    group.auction_start_date ||
    group.start_date ||
    null;

  const time =
    group.auction_start_time ||
    group.start_time ||
    (group.auction_settings && group.auction_settings.start_time) ||
    '07:00';

  if (!date) return null;

  const t = /^\d{2}:\d{2}(:\d{2})?$/.test(time)
    ? (time.length === 5 ? `${time}:00` : time)
    : '07:00:00';

  return DateTime.fromISO(`${date}T${t}`, { zone: tz, setZone: true });
}, []);
  // Compute a window from group schedule if API didn't give one
  const computeWindowFromGroup = (group) => {
    try {
      const zone =
        group.auction_timezone ||
        group.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;

      const dateISO = group.auction_start_date || group.start_date || null;
      const timeStr = group.auction_start_time || group.start_time || null;
      if (!dateISO || !timeStr) return null;

      // Parse first scheduled start in the group’s zone
      let firstStart = DateTime.fromISO(`${dateISO}T${timeStr}`, { zone, setZone: true });
      if (!firstStart.isValid) return null;

      // Duration (minutes) from various possible field names; default 45
      const durMin = Number(
        group.auction_window_minutes ??
        group.auction_duration_minutes ??
        group.bidding_window_minutes ??
        group.bidding_duration_minutes ??
        45
      );
      const step = (group.contribution_frequency || 'weekly').toLowerCase();

      // Find the most recent window whose end > now (i.e., current or next)
      const now = DateTime.now().setZone(zone);
      let start = firstStart;
      let end   = start.plus({ minutes: durMin });

      const advance = () => {
        if (step === 'fortnightly') start = start.plus({ weeks: 2 });
        else if (step === 'monthly') start = start.plus({ months: 1 });
        else start = start.plus({ weeks: 1 }); // default weekly
        end = start.plus({ minutes: durMin });
      };

      while (end <= now) {
        advance();
        if (start.diff(firstStart, 'years').years > 5) break;
      }

      return {
        start_time: start.toISO(),
        end_time: end.toISO(),
        server_time: now.toISO(),
      };
    } catch {
      return null;
    }
  };


  // ------------------------- Data loaders -------------------------

  const fetchBidInfoForGroups = useCallback(async (groupList) => {
  const info = {};
  const times = {};
  const statuses = {};

  for (const group of groupList) {
    const gid = group.id ?? group.group_id;
    if (!gid) continue;

    // Fast path: if the backend already provided next/active window metadata,
    // we can render the timers without hitting /bidding/status for every group.
    const serverActive = !!group?.auction_active && !!group?.active_bid_start_time && !!group?.active_bid_end_time;
    const serverNext = !serverActive && !!group?.next_bid_start_time && !!group?.next_bid_end_time;
    if (serverActive) {
      times[gid] = {
        start_time: group.active_bid_start_time,
        end_time: group.active_bid_end_time,
        auction_active: true,
        period_number: group.active_bid_period_number ?? null,
      };
      statuses[gid] = { auction_active: true, period_number: group.active_bid_period_number ?? null };
    } else if (serverNext) {
      times[gid] = {
        start_time: group.next_bid_start_time,
        end_time: group.next_bid_end_time,
        auction_active: false,
        period_number: group.next_bid_period_number ?? null,
      };
      statuses[gid] = { auction_active: false, period_number: group.next_bid_period_number ?? null };
    }

    let started = false;
    let win = null;

    // 1) status first
    // Only needed for ACTIVE groups (live bidding) or when we don't have server metadata.
    try {
      const shouldFetchStatus = serverActive || (!serverActive && !serverNext);
      if (!shouldFetchStatus) throw new Error('skip_status');
      const r = await apiFetch(`/api/bidding/status/${gid}`);
      if (r.ok) {
        const s = await safeJson(r);
        if (s) statuses[gid] = s;
        // accept both shapes: { started, window } or flat
        started = !!(s?.started ?? s?.auction_active);
        win = (function selectWindow(obj) {
          if (!obj || typeof obj !== 'object') return null;
          const st = obj.start_time || obj.startTime || obj.start || obj.window_start || obj.start_at;
          const et = obj.end_time   || obj.endTime   || obj.end   || obj.window_end   || obj.end_at;
          const sv = obj.server_time || obj.serverTime || null;
          const ac = Object.prototype.hasOwnProperty.call(obj, 'auction_active') ? !!obj.auction_active : null;
          if (st && et) return { start_time: st, end_time: et, server_time: sv, auction_active: ac };
          if (obj.window && (obj.window.start_time || obj.window.start) && (obj.window.end_time || obj.window.end)) {
            return {
              start_time: obj.window.start_time || obj.window.start,
              end_time: obj.window.end_time || obj.window.end,
              server_time: sv,
              auction_active: ac,
            };
          }
          const p = obj.period || obj.current_window || null;
          if (p && (p.start_time || p.start) && (p.end_time || p.end)) {
            return {
              start_time: p.start_time || p.start,
              end_time: p.end_time || p.end,
              server_time: sv,
              auction_active: ac,
            };
          }
          return null;
        })(s);
        if (win) times[gid] = win;
      } else if (DEBUG_BIDDING) {
        console.debug(`status/${gid} -> ${r.status}`);
      }
    } catch (e) {
      if (DEBUG_BIDDING) console.debug(`⚠️ status/${gid} failed:`, e?.message);
    }

    // 2) Only call /current if we think a window is live now OR status says started
    let callCurrent = started;
    try {
      if (!callCurrent && times[gid]?.start_time && times[gid]?.end_time) {
        const now = Date.now();
        const st = Date.parse(times[gid].start_time);
        const et = Date.parse(times[gid].end_time);
        if (isFinite(st) && isFinite(et) && now >= st && now <= et) {
          callCurrent = true;
        }
      }
    } catch {}

    if (callCurrent) {
      try {
        const r2 = await apiFetch(`/api/groups/auction/${gid}/current`);
        if (r2.status === 404) {
          // normal: no active window — record explicit null and do not log
          info[gid] = null;
        } else if (r2.ok) {
          const c = await safeJson(r2);
          if (c) {
            if (c.current_period >= 1 || c.is_live || (c.period != null && c.period >= 1)) info[gid] = c;
            if (!times[gid]) {
              const win2 = (function selectWindow(obj) {
                if (!obj || typeof obj !== 'object') return null;
                const st = obj.start_time || obj.startTime || obj.start || obj.window_start || obj.start_at;
                const et = obj.end_time   || obj.endTime   || obj.end   || obj.window_end   || obj.end_at;
                const sv = obj.server_time || obj.serverTime || null;
                if (st && et) return { start_time: st, end_time: et, server_time: sv };
                return null;
              })(c);
              if (win2) times[gid] = win2;
            }
          }
        } else if (DEBUG_BIDDING) {
          console.debug(`current/${gid} -> ${r2.status}`);
        }
      } catch {
        // network only; ignore
      }
    }

    // 3) Fallback — compute from group schedule if we still don't have times
    if (!times[gid]) {
      try {
        const g = group.auction_settings ? group : await apiFetch(`/api/groups/${gid}`).then(safeJson);
        const composed = { ...g, ...(g?.auction_settings || {}) };
        const win3 = computeWindowFromGroup(composed);
        if (win3) {
          // Carry forward the server's active flags even when we compute a fallback window.
          // This prevents UI from treating a *future* window as if the just-finished window is still active.
          const stPayload = statuses[gid] || null;
          times[gid] = {
            ...win3,
            auction_active:
              stPayload && Object.prototype.hasOwnProperty.call(stPayload, 'auction_active')
                ? !!stPayload.auction_active
                : null,
            is_active:
              stPayload && Object.prototype.hasOwnProperty.call(stPayload, 'is_active')
                ? !!stPayload.is_active
                : null,
            period_number: stPayload?.period_number ?? null,
          };
          if (DEBUG_BIDDING) console.debug(`🧮 computed window for ${gid}:`, times[gid]);
        }
      } catch { /* ignore */ }
    }
  }

  // Only update state when it actually changed (prevents UI from looking like a full refresh)
  const nextInfoHash = stableHash(info);
  if (nextInfoHash !== bidInfoHashRef.current) {
    bidInfoHashRef.current = nextInfoHash;
    setBidInfo(info);
  }

  const nextTimesHash = stableHash(times);
  if (nextTimesHash !== timesHashRef.current) {
    timesHashRef.current = nextTimesHash;
    setBiddingTimes(times);
  }

  // Merge statuses but avoid changing reference when nothing changed
  setBidStatusMap((prev) => {
    const merged = { ...prev };
    let changed = false;
    for (const [k, v] of Object.entries(statuses || {})) {
      const prevV = prev?.[k];
      const pv = prevV ? JSON.stringify(prevV) : '';
      const nv = v ? JSON.stringify(v) : '';
      if (pv !== nv) {
        merged[k] = v;
        changed = true;
      }
    }
    if (!changed) return prev;
    const nextStatusHash = stableHash(merged);
    if (nextStatusHash === statusHashRef.current) return prev;
    statusHashRef.current = nextStatusHash;
    return merged;
  });
  if (DEBUG_BIDDING) {
    console.debug('🔎 biddingTimes loaded:', times);
    console.debug('🔎 bidInfo loaded:', info);
  }
}, []);

  // 🔹 Load actionable items for the user (winner/payout aware + push)
const loadActionItems = useCallback(async (myGroups) => {
  const items = [];

  // ----- Push action (can appear even if there are no groups) -----
  if (pushState && pushState.ok === false) {

    // If push failed only due to auth not being ready yet, don't show an error card on Home.
    // We'll retry silently later.
    if (pushState.reason === 'auth' || (pushState.reason === 'server' && (pushState.status === 401 || pushState.status === 403))) {
      // no push action item
    } else {
    // Prioritize iOS install hint, then denied, then unsupported/no-key, then errors
    const r = pushState.reason;

    const details =
      r === 'server'
        ? (pushState.status ? `Server error (HTTP ${pushState.status})` : 'Server error')
        : (r === 'exception' || r === 'error')
          ? `${pushState.errorName || 'Error'}${pushState.errorMessage ? `: ${pushState.errorMessage}` : ''}`
          : '';

    if (r === 'enable') {
      items.push({ type: 'push-enable', reason: r });
    } else if (r === 'ios-install-required') {
      items.push({ type: 'push-ios', reason: r });
    } else if (r === 'denied') {
      items.push({ type: 'push-denied', reason: r });
    } else if (r === 'unsupported' || r === 'no-key') {
      items.push({ type: 'push-unsupported', reason: r });
    } else if (r === 'sw-html' || r === 'sw-missing') {
      // Service worker file is missing or being rewritten to index.html (common on Amplify SPA rewrites)
      items.push({ type: 'push-error', reason: r, details });
    } else if (r === 'server' || r === 'error' || r === 'exception') {
      items.push({ type: 'push-error', reason: r, details });
    }
  }
    }



  // If there are no groups, we still show push item(s) above

  // If there are no groups, we still show push item(s) above
  if (!Array.isArray(myGroups) || !myGroups.length) {
    setActionItems(items);
    return;
  }

  // Helper to normalize different API shapes into an array of accounts
  const extractAccounts = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.rows)) return payload.rows;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  // Fetch user-level bank accounts once
  let userBanks = [];
  try {
    let rUser = await apiJson(`/api/bank/me`);
    if (!rUser.ok) rUser = await apiJson(`/api/bank/user/${user?.id}`);
    if (rUser.ok) userBanks = extractAccounts(rUser.data);
  } catch { /* ignore */ }

  // For each group we’ll compute:
  //  - unpaid payments for THIS user (payer view)
  //  - winner status for THIS user and if latest winner period is unpaid (payout view)
  const perGroupPromises = myGroups.map(async (g) => {
    const gid = g.id ?? g.group_id;
    // 🔸 Membership-level bank check (ALWAYS per-membership; no aggregate)
    try {
      // 1) Has bidding started?
      const statusRes = await apiJson(`/api/bidding/status/${gid}`);
      let started = !!(statusRes?.data?.started ?? statusRes?.started);

      if (!started) {
        // Fallback: compute start of period #1 in the group's TZ
        const p1 = getPeriod1Start(g);
        if (p1 && p1.isValid) {
          const nowInTz = DateTime.now().setZone(p1.zone); // use same zone as p1
          started = nowInTz >= p1;
        }
      }

      if (started) {
        // 2) Which membership indexes are mine? (fallback to [1] if unknown)
        const indices = getMyMembershipIndices(g);
        const list = (Array.isArray(indices) && indices.length) ? indices : [1];

        // 3) Do I have at least one user bank account?
        const hasAnyBank = Array.isArray(userBanks) && userBanks.length > 0;

        // Helper that accepts both API shapes (data-wrapped or flat) and both param names
        const fetchIsLinked = async (gid, idx) => {
          const tryOnce = async (q) => {
            const r = await apiJson(`/api/bank/link-status/${gid}?${q}`);
            const d = r?.data ?? r; // support {data:{...}} and flat {...}
            // accept common key variants
            return !!(d?.isLinked ?? d?.linked ?? d?.is_linked ?? d?.data?.isLinked);
          };
          // camelCase then snake_case
          return (await tryOnce(`membershipIndex=${idx}`)) || (await tryOnce(`membership_index=${idx}`));
        };

        for (const idx of list) {
          const linked = await fetchIsLinked(gid, idx);
          if (!linked) {
            items.push({
              type: hasAnyBank ? 'bank-link' : 'bank-user',
              group: g,
              membershipIndex: Number(idx) || 1,
            });
          }
        }
      }
    } catch {
      /* non-blocking */
    }

    // 1) Pull all payments for the group
    let payRows = [];
    try {
      const r = await apiJson(`/api/payments/${gid}`);
      if (r.ok) payRows = Array.isArray(r.data?.rows) ? r.data.rows : [];
    } catch { /* ignore */ }

    // Prefer to only count "your" unpaid payments if user_id is provided
    const myUnpaid = payRows.filter((row) => {
      if (!row || row.status === 'paid') return false;

      // Only count rows that belong to the logged-in user (some admin APIs may return other users too)
      if (row.user_id != null && String(row.user_id) !== String(user?.id)) return false;

      // If backend provides winner info, do not count "winner slot" as something the winner must pay.
      const isWinnerSlot =
        row.winner_user_id != null &&
        String(row.user_id) === String(row.winner_user_id) &&
        Number(row.membership_index || 1) === Number(row.winner_membership_index || 1);

      return !isWinnerSlot;
    });

    // Add a "Payment due" action item if there are unpaid rows for you in this group
    if (myUnpaid.length > 0) {
      // due_date may come back as ISO OR MySQL DATETIME ("YYYY-MM-DD HH:mm:ss").
      // Never rely on Date.parse (Safari breaks on SQL datetime). Use Luxon parser.
      const next = (
        myUnpaid
          .map(x => x.due_date)
          .filter(Boolean)
          .map(raw => ({ raw, dt: parseAnyDateTime(raw, { assumeZone: 'utc' }) }))
          .filter(x => x.dt)
          .sort((a, b) => a.dt.toMillis() - b.dt.toMillis())[0]
      ) || null;
      items.push({
        type: 'payment',
        group: g,
        count: myUnpaid.length,
        // Store a stable ISO for rendering
        nextDueISO: next?.dt ? next.dt.toUTC().toISO() : null,
      });
    }

    // 3) Organiser payout account missing (only for groups you own)
    if (String(g.owner_id) === String(user?.id)) {
      try {
        const r = await apiJson(`/api/bank/group/${gid}`);
        const list = r.ok && Array.isArray(r.data) ? r.data : [];
        if (list.length === 0) {
          items.push({ type: 'bank-organiser', group: g });
        }
      } catch { /* ignore */ }
    }

    // 4) Winner reviews pending (only if this user is allowed to review)
      try {
        if (!canReviewPaymentsForGroup(user, g)) {
          // ✅ normal members: do not call /review at all (prevents 403 spam)
        } else {
          const winners = await apiJson(`/api/bidding/winners/${gid}`);

          if (winners.ok && Array.isArray(winners.data) && winners.data.length > 0) {
            const latest = winners.data.reduce(
              (max, r) => Math.max(max, Number(r.period_number || 0)),
              0
            );

            if (Number.isFinite(latest) && latest > 0) {
              // ✅ use apiFetch so we can silently ignore 403 without throwing
              const res = await apiFetch(`/api/payments/${gid}/${latest}/review`);

              if (res.status === 403) {
                // forbidden for this user — skip silently
              } else {
                const payload = await safeJson(res); // you already have safeJson above
                const rows = Array.isArray(payload?.data)
                  ? payload.data
                  : (Array.isArray(payload) ? payload : []);

                if (res.ok && Array.isArray(rows)) {
                  const need = rows.filter(
                    row => row.can_review && (!row.points_given || Number(row.points_given) === 0)
                  );
                  if (need.length > 0) {
                    items.push({ type: 'review', group: g, period: latest, count: need.length });
                  }
                }
              }
            }
          }
        }
      } catch {
        /* ignore */
      }


    return null;
  });

  await Promise.all(perGroupPromises);

  // Priority: push hints first → payments → bank-user → bank-link → bank-organiser → review
  const pri = {
    'push-ios': 0, 'push-denied': 0, 'push-unsupported': 0, 'push-error': 0,
    'payment': 1, 'bank-user': 2, 'bank-link': 3, 'bank-organiser': 4, 'review': 5
  };
  items.sort((a,b)=> (pri[a.type]-pri[b.type]) || String(a.group?.name||'').localeCompare(String(b.group?.name||'')));

  setActionItems(items);
}, [user?.id, pushState]);

  // 🔔 Ensure push subscription after login (and on SW expiration message)
  const ensurePush = useCallback(async () => {
    // Ensure the session cookie is actually valid before attempting push subscribe.
    // On first load, we may have a cached user in storage but no active server session yet.
    const me = await refreshSessionUser().catch(() => null);
    if (!me?.id) {
      // Don't show an error card yet; we'll retry later once session is established.
      return;
    }

    if (!isPushSupported()) {
      setPushState({ ok: false, reason: 'unsupported' });
      return;
    }
    const result = await registerPush();

    // If the server replied 401/403 during subscribe, treat as transient auth-not-ready and retry shortly.
    if (result && result.ok === false && result.reason === 'auth') {
      setPushState(result);
      window.setTimeout(() => {
        // Retry silently; don't spam prompts
        registerPush().then(setPushState).catch(() => {});
      }, 2500);
      return;
    }

    setPushState(result);
  }, []);

  useEffect(() => {
    if (!user) return;
    // Only auto-register if permission already granted.
    // On Android/Chrome, requesting permission on page load can be blocked or treated as spam.
    // User can always tap Retry to trigger the prompt.
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      ensurePush();
    } else {
      // Show an action item prompting the user to enable notifications
      setPushState({ ok: false, reason: 'enable' });
    }

    // Re-register if SW says subscription expired/rotated
    const onSwMessage = (e) => {
      if (e?.data?.type === 'PUSH_SUBSCRIPTION_EXPIRED') {
        registerPush().then(setPushState).catch(() => {});
      }
    };
    try {
      navigator.serviceWorker?.addEventListener?.('message', onSwMessage);
    } catch {}
    return () => {
      try { navigator.serviceWorker?.removeEventListener?.('message', onSwMessage); } catch {}
    };
  }, [user, ensurePush]);

  // Handy helpers for push hints
  const showIosInstallHelp = useCallback(() => {
    window.alert(
      'To enable notifications on iPhone/iPad:\n\n' +
      '1) Open this site in Safari\n' +
      '2) Tap the Share icon → Add to Home Screen\n' +
      '3) Open the app from your Home Screen and sign in\n' +
      '4) When prompted, tap Allow'
    );
  }, []);

  const retryPushSetup = useCallback(async () => {
    // Give the browser a short tick after user interaction before SW APIs (reduces InvalidStateError)
    await new Promise((r) => setTimeout(r, 50));
    const r = await registerPush();
    // Treat 401/403 as transient (session not hydrated yet). We'll retry silently.
    if (!r?.ok && r?.reason === 'server' && (r.status === 401 || r.status === 403)) {
      // try again shortly without showing an error card
      setTimeout(() => { ensurePush(); }, 1200);
      return;
    }
    setPushState(r);
  }, []);

  const resetPushSetup = useCallback(async () => {
    try {
      setPushState({ ok: false, reason: 'resetting' });
      await unregisterPush({ unregisterServiceWorker: true });
    } catch {
      // ignore
    }

    // small delay to let SW unregister settle
    await new Promise((resolve) => setTimeout(resolve, 350));

    const r = await registerPush();
    // Treat 401/403 as transient (session not hydrated yet). We'll retry silently.
    if (!r?.ok && r?.reason === 'server' && (r.status === 401 || r.status === 403)) {
      // try again shortly without showing an error card
      setTimeout(() => { ensurePush(); }, 1200);
      return;
    }
    setPushState(r);
  }, []);

  const showDeniedHelp = useCallback(() => {
    window.alert(
      'Notifications are blocked.\n\n' +
      'On iPhone/iPad (PWA): Settings → Notifications → ChitConnect → Allow Notifications.\n' +
      'On Android/Chrome: Site settings → Notifications → Allow.\n\n' +
      'Then return to the app and tap “Retry”.'
    );
  }, []);

  // Robust groups loader with 401/403 handling
  const fetchMyGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      // First attempt
      // Home-optimised endpoint includes next/active auction window metadata
      // so we can render quickly without N+1 schedule calls.
      let res = await apiFetch('/api/groups/my-home');

      // If auth isn't ready yet (common on mobile right after returning from live bidding),
      // refresh session once and retry.
      if (!res.ok && (res.status === 401 || res.status === 403)) {
        try { await refreshSessionUser(); } catch (e) {}
        res = await apiFetch('/api/groups/my-home');
      }

      if (!res.ok) {
        console.warn('groups/my returned', res.status);
        // Don't aggressively wipe groups on transient failures; just keep current state.
        setGroupsLoading(false);
        return;
      }

      const data = await safeJson(res);
      const list = Array.isArray(data) ? data : [];

      // Avoid flicker/re-order "refresh" feel: only update groups when something actually changed
      const gHash = (list || []).map((g) => [
        g?.id,
        g?.name,
        g?.status,
        g?.active_bid_start_time,
        g?.active_bid_end_time,
        g?.active_bid_period_number,
        g?.next_bid_start_time,
        g?.next_bid_end_time,
        g?.next_bid_period_number,
        g?.organiser_benefit_period
      ].join('~')).join('||');

      if (gHash !== groupsHashRef.current) {
        groupsHashRef.current = gHash;
        setGroups(list);
      }

      // ✅ Make the page feel fast:
      // stop blocking the UI on extra per-group calls (status/schedule/payments/banks).
      setGroupsLoading(false);

      // Fetch per-group progress in the background (non-blocking)
      (async () => {
        try {
          const progRes = await apiFetch('/api/groups/progress');
          const progData = await safeJson(progRes);
          if (progRes.ok) setProgressById(progData?.progressByGroupId || {});
        } catch {
          // ignore progress errors
        }
      })();

      // Enrich bidding/status/action-items in the background (non-blocking)
      // These are expensive (N+1 calls), so do them after the first paint.
      setTimeout(() => {
        if (list.length) {
          fetchBidInfoForGroups(list).catch(() => {});
          loadActionItems(list).catch(() => {});
        } else {
          loadActionItems([]).catch(() => {});
        }
      }, 0);

    } catch (err) {
      console.error('❌ Failed to fetch my groups:', err);
      setGroupsLoading(false);
      // keep existing groups on error; still show action items hint
      loadActionItems(groups || []).catch(() => {});
    }
  }, [fetchBidInfoForGroups, loadActionItems, groups]);

  const fetchAllUsers = useCallback(async () => {
  if (!isSuperadmin) return;
    try {
      const res = await apiFetch('/api/users');
      if (!res.ok) {
        setUsers([]);
        return;
      }
      const data = await safeJson(res);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Failed to fetch users:', err);
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      await Promise.all([fetchMyGroups(), fetchAllUsers()]);
    })();
  }, [user, fetchMyGroups, fetchAllUsers]);

  // Keep action items in sync if groups or push state change elsewhere
  useEffect(() => {
    if (groups && groups.length) loadActionItems(groups);
    else loadActionItems([]); // still show push hints with no groups
  }, [groups, pushState, loadActionItems]);

  // 🔄 Refresh action items if another page updates bank accounts
  useEffect(() => {
    const onBankUpdated = () => {
      if (groups && groups.length) loadActionItems(groups);
    };
    window.addEventListener('chit:bank-updated', onBankUpdated);
    return () => window.removeEventListener('chit:bank-updated', onBankUpdated);
  }, [groups, loadActionItems]);

  // 🔄 Refresh on tab focus (covers adding bank in another tab)
  useEffect(() => {
    const onFocus = () => {
      if (groups && groups.length) loadActionItems(groups);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [groups, loadActionItems]);

  
  // -------------------- Refresh auction status (smart polling) --------------------
  // We still poll as a safety net, but:
  //  - Less frequently (sockets handle real-time events)
  //  - Pause polling when the tab/app is not visible (mobile-friendly)
  //  - Refresh immediately on focus/visibility return
  useEffect(() => {
    if (!groups?.length) return;

    let intervalId = null;

    const refresh = () => {
      fetchBidInfoForGroups(groups).catch(() => {});
    };

    const start = () => {
      if (intervalId) return;
      // run once immediately when starting
      refresh();
      intervalId = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        refresh();
      }, 60000);
    };

    const stop = () => {
      if (!intervalId) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        // refresh immediately when user returns
        refresh();
        start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, [groups, fetchBidInfoForGroups]);

// -------------------- Bidding popup is MANUAL only --------------------
// We intentionally do NOT auto-open the popup. Users open it via the "Open bidding" button
// inside the group card, and the Close button should always keep it closed.

  // Keep biddingTimes[gid].end_time in sync with live extensions
  useEffect(() => {
    const onExtended = ({ groupId, newEndTime, periodNumber }) => {
      setBiddingTimes(prev => {
        const win = prev?.[groupId];
        if (!win) return prev;
        const prevEnd = win.end_time ? Date.parse(win.end_time) : 0;
        const nextEnd = newEndTime ? Date.parse(newEndTime) : 0;
        if (nextEnd <= prevEnd) return prev;
        return {
          ...prev,
          [groupId]: {
            ...win,
            end_time: newEndTime,
            auction_active: true,
            period_number: periodNumber ?? win.period_number,
          },
        };
      });
    };

    socket.on('auctionExtended', onExtended);
    return () => socket.off('auctionExtended', onExtended);
  }, []);

  // Close the popup immediately when a winner is finalized (server-side)
  useEffect(() => {
    const onWinner = (payload) => {
      const gid = payload?.groupId ?? payload?.group_id;
      if (!gid) return;

      const activeGid = (activeBiddingGroup?.id ?? activeBiddingGroup?.group_id);
      if (showBiddingPopup && activeGid && String(activeGid) === String(gid)) {
        setShowBiddingPopup(false);
        setActiveBiddingGroup(null);
        const status = biddingTimes[gid];
        const openKey = `${gid}:${(status?.period_number ?? payload?.period_number ?? 'na')}`;
        setPopupShownForGroup(openKey);
      }

      setBiddingTimes(prev => {
        const win = prev?.[gid];
        if (!win) return prev;
        return { ...prev, [gid]: { ...win, auction_active: false } };
      });

      fetchBidInfoForGroups(groups).catch(() => {});
      loadActionItems(groups).catch(() => {});
    };

    socket.on('winnerFinalized', onWinner);
    return () => socket.off('winnerFinalized', onWinner);
  }, [
    showBiddingPopup,
    activeBiddingGroup,
    biddingTimes,
    groups,
    fetchBidInfoForGroups,
    loadActionItems
  ]);

  // -------------- Live countdowns (start + end) --------------------
  
  // --------------------------- Helpers ----------------------------
  // Prefer organiser details coming from the groups API (organiser_name/email/mobile).
  // Fall back to local 'users' list only if needed (e.g., older API responses).
  const getOrganiserDetails = (group) => {
    if (!group) return { name: 'Unknown', email: 'N/A', mobile: 'N/A' };

    const nameFromGroup =
      group.organiser_name ||
      group.organiserName ||
      group.owner_name ||
      group.ownerName ||
      '';
    const emailFromGroup =
      group.organiser_email ||
      group.organiserEmail ||
      group.owner_email ||
      group.ownerEmail ||
      '';
    const mobileFromGroup =
      group.organiser_mobile ||
      group.organiserMobile ||
      group.owner_mobile ||
      group.ownerMobile ||
      '';

    if (nameFromGroup || emailFromGroup || mobileFromGroup) {
      return {
        name: String(nameFromGroup || 'Unknown').trim() || 'Unknown',
        email: emailFromGroup || 'N/A',
        mobile: mobileFromGroup || 'N/A',
      };
    }

    const ownerId = group.owner_id;
    const organiser = users.find((u) => u.id === ownerId);
    if (!organiser) return { name: 'Unknown', email: 'N/A', mobile: 'N/A' };
    return {
      name: `${organiser.given_name} ${organiser.surname}`.trim(),
      email: organiser.email,
      mobile: organiser.mobile,
    };
  };
  // ---------------------------- Render ----------------------------
  return (
    <div className="main-content">
      <div style={{ textAlign: "center", margin: "0 auto 10px" }}>
        <img src={logo} alt="ChitConnect" className="logo" style={{ display: "inline-block" }} />
      </div>
      <Greeting user={effectiveUser} />
        
      <div style={{ textAlign: "center", fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
        Build: {process.env.REACT_APP_BUILD_TAG || "chit126-v7.1"}
      </div>
<h2 className="page-title">My Chit Groups</h2>
        

      {/* ===== Your Action Items ===== */}
      {Array.isArray(actionItems) && actionItems.length > 0 && (
        <div className="action-strip" style={{marginBottom:'16px'}}>
          <h2 className="section-title" style={{margin:'0 0 8px'}}>Your Action Items</h2>
          <div
            className="action-grid"
            style={{
              display:'grid',
              gap:'12px',
              gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))'
            }}
          >
            {actionItems.slice(0, actionLimit).map((it, idx) => {
              const g = it.group;
              const title =
                it.type === 'payment'         ? 'Payment due'
              : it.type === 'bank-user'       ? 'Add your bank account'
              : it.type === 'bank-link'       ? 'Link your bank to this group'
              : it.type === 'bank-organiser'  ? 'Add payout bank account'
              : it.type === 'push-enable'     ? 'Enable notifications'
              : it.type === 'push-ios'        ? 'Enable notifications (iPhone/iPad)'
              : it.type === 'push-denied'     ? 'Notifications are blocked'
              : it.type === 'push-unsupported'? 'Notifications not supported'
              : it.type === 'push-error'
    ? (it.reason === 'sw-html'
        ? 'Notifications can’t start because /sw.js is being rewritten to the app page. Fix Amplify rewrites, then tap Retry.'
        : it.reason === 'sw-missing'
          ? 'Notifications can’t start because /sw.js is missing (service worker file not found). Fix deploy, then tap Retry.'
          : it.reason === 'server'
            ? 'Notifications setup failed when saving your subscription on the server. Tap Retry or Reset.'
            : (it.reason === 'exception' || it.reason === 'error')
              ? 'Notifications setup failed during Service Worker / Push registration. See details below, then tap Reset.'
              : 'We had trouble registering for notifications. Try again.')
              : `${it.count} confirmation${it.count>1?'s':''} pending (Period #${it.period})`;


              const sub =
                it.type === 'payment'
                  ? (it.nextDueISO
                      ? (() => {
                          const dt =
                            parseAnyDateTime(it.nextDueISO, { assumeZone: 'utc' }) ||
                            (it.nextDueISO ? DateTime.fromISO(it.nextDueISO, { setZone: true }) : null);
                          return dt
                            ? `Next due: ${dt.toLocal().toFormat('ccc d LLL, h:mm a')}`
                            : `${it.count} unpaid payment${it.count > 1 ? 's' : ''}`;
                        })()
                      : `${it.count} unpaid payment${it.count > 1 ? 's' : ''}`)
                : it.type === 'bank-user'
                  ? 'Add a bank account once so you can receive payouts.'
                : it.type === 'bank-link'
                  ? 'Select which of your bank accounts to use for this group.'
                : it.type === 'bank-organiser'
                  ? 'Add a payout account so winners can be paid.'
                : it.type === 'push-enable'
                  ? 'Get reminders for payments and bidding.'
                : it.type === 'push-ios'
                  ? 'Install the app to Home Screen to enable notifications on iOS.'
                : it.type === 'push-denied'
                  ? 'Allow notifications in browser settings, then Retry.'
                : it.type === 'push-unsupported'
                  ? 'Try Chrome on Android/Desktop for push support.'
                : it.type === 'push-error'
                  ? 'Tap Reset, then Retry.'
                  : 'Open payment reviews and confirm the winner.';

              const go = () => {
                if (it.type === 'payment') window.location.href = '/payments';
                else if (it.type === 'bank-user') window.location.href = '/bank-details';
                else if (it.type === 'bank-link') window.location.href =
                  `/bank-details?groupId=${g?.id ?? g?.group_id ?? ''}&membershipIndex=${it.membershipIndex ?? ''}`;
                else if (it.type === 'bank-organiser') window.location.href = '/bank-details';
                else if (it.type === 'push-ios') showIosInstallHelp();
                else if (it.type === 'push-denied') showDeniedHelp();
                else if (it.type === 'push-unsupported') window.alert('Your current browser does not support Web Push.');
                else if (it.type === 'push-enable') retryPushSetup();
                else if (it.type === 'push-error') retryPushSetup();
                else window.location.href = '/payments/review';
              };

              return (
                <div key={idx} className="group-card" style={{padding:'12px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'6px'}}>
                    <img src={logo} alt="" style={{width:24, height:24, opacity:0.8}} />
                    <strong style={{fontSize:'15px'}}>{title}</strong>
                  </div>
                  <div style={{fontSize:'14px', marginBottom:'6px'}}>
                    {g && <div style={{fontWeight:600}}>{g?.name || `Group #${g?.id}`}</div>}
                    <div style={{opacity:0.8}}>{sub}</div>
                    {it.details && (
                      <div style={{ fontSize: 12, marginTop: 6, opacity: 0.95, color: '#b00020' }}>
                        {it.details}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="action-button" onClick={go} style={{ flex: '1 1 140px' }}>
                      {it.type === 'payment'          ? 'Open My Payments'
                        : it.type === 'bank-user'     ? 'Add Bank Account'
                        : it.type === 'bank-link'     ? 'Link Bank to Group'
                        : it.type === 'bank-organiser'? 'Add Payout Account'
                        : it.type === 'push-ios'      ? 'How to install'
                        : it.type === 'push-denied'   ? 'Open Settings'
                        : it.type === 'push-unsupported' ? 'Learn more'
                        : it.type === 'push-enable'   ? 'Retry'
                        : it.type === 'push-error'    ? 'Retry'
                        : 'Open Reviews'}
                    </button>

                    {(it.type === 'push-enable' || it.type === 'push-error' || it.type === 'push-denied') && (
                      <button
                        className="action-button"
                        onClick={resetPushSetup}
                        style={{ flex: '1 1 120px', opacity: 0.85 }}
                        title="Clear service worker + subscription and try again"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load more action items */}
          {actionItems.length > actionLimit && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <button
                className="action-button"
                onClick={() => setActionLimit((n) => n + 5)}
                style={{ maxWidth: 220 }}
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {groupsLoading ? (
        <p>Loading your groups…</p>
      ) : groups.length === 0 ? (
        <p>You’re not part of any chit groups yet.</p>
      ) : (
        <div className="group-card-list">
          {sortedGroups.map((group) => {
            const gid = group.id ?? group.group_id;
            const organiser = getOrganiserDetails(group);

            const frequency = group.contribution_frequency || 'monthly';
            const pluralMap = { weekly: 'weeks', fortnightly: 'fortnights', monthly: 'months' };
            const durationLabel = `${group.duration} ${pluralMap[frequency] || `${frequency}s`}`;

            const status = biddingTimes[gid];
            const s = bidStatusMap[gid];
            const now = DateTime.utc();

            const startISO = s?.start_time || status?.start_time || null;
            const endISO   = s?.end_time   || status?.end_time   || null;

            const start = startISO ? DateTime.fromISO(startISO, { setZone: true }) : null;
            const end   = endISO   ? DateTime.fromISO(endISO,   { setZone: true }) : null;

            const current = bidInfo[gid];
            const organiserPeriod = Number(group.organiser_benefit_period || 0) || null;
            const currentPeriod = (current?.current_period ?? current?.period);
            const isBenefitPeriod = !!(organiserPeriod && currentPeriod != null && Number(currentPeriod) === organiserPeriod);

            const periodLive = !!(!isBenefitPeriod && current && current.is_live === true);
            const windowLive = !!(start && end && now >= start && now <= end);
            const serverSaysInactive = !!(s && (
              s.auction_active === false ||
              s.is_active === false ||
              s.bidding_enabled === false ||
              s.is_organiser_benefit_period === true
            ));

            const isLiveNow = !serverSaysInactive && (windowLive || periodLive);

            const nextDateTimeLabel =
              !isLiveNow && start
                ? start.toLocal().toFormat('ccc d LLL, h:mm a')
                : '';

                        const pProg = progressById?.[gid];
            const finishedPeriods = Number(pProg?.finished ?? 0);
            const uiStatus = (() => {
              const raw = String(group.status || '').toLowerCase();
              if (raw === 'approved' && (finishedPeriods > 0 || isLiveNow)) return 'active';
              return group.status || 'approved';
            })();

return (
              <div key={gid} className="group-card">
                {(() => {
                  // IMPORTANT: always use the normalized group id from the outer scope (id ?? group_id).
                  // Some APIs return `group_id` only; redeclaring `gid = group.id` breaks bidding status
                  // and can incorrectly hide/disable the bidding popup.

                  const derivePeriods = () => {
                    const info = bidInfo[gid] || {};
                    const apiCurrent = (info.current_period ?? info.period);
                    const apiNext    = (info.next_period);

                    if (apiCurrent != null && apiNext != null) {
                      return { current: Number(apiCurrent), next: Number(apiNext) };
                    }

                    const p1 = getPeriod1Start(group);
                    if (!p1 || !p1.isValid) return { current: null, next: null };

                    const zoneNow = DateTime.now().setZone(p1.zoneName);
                    const status = biddingTimes[gid];
                    const start = status?.start_time ? DateTime.fromISO(status.start_time, { setZone: true }) : null;
                    const end   = status?.end_time   ? DateTime.fromISO(status.end_time,   { setZone: true }) : null;

                    const freq = (group.contribution_frequency || 'weekly').toLowerCase();
                    const step = (dt) => {
                      if (freq === 'fortnightly') return dt.plus({ weeks: 2 });
                      if (freq === 'monthly')     return dt.plus({ months: 1 });
                      return dt.plus({ weeks: 1 });
                    };

                    let anchor = start && start.isValid ? start : zoneNow;

                    if (anchor < p1) return { current: null, next: 1 };

                    let n = 1;
                    let curStart = p1;
                    let curEnd   = step(curStart);
                    let guard = 0;
                    while (anchor >= curEnd && guard < 200) {
                      curStart = step(curStart);
                      curEnd   = step(curStart);
                      n += 1;
                      guard += 1;
                    }

                    const insideWindow =
                      start && end && start.isValid && end.isValid && zoneNow >= start && zoneNow <= end;

                    const current = insideWindow ? n : null;
                    const next    = insideWindow ? n + 1 : n;

                    return {
                      current: apiCurrent != null ? Number(apiCurrent) : current,
                      next:    apiNext    != null ? Number(apiNext)    : next,
                    };
                  };

                  const { current: derivedCurrent, next: derivedNext } = derivePeriods();

                  const currentPeriod = (bidInfo[gid]?.current_period ?? bidInfo[gid]?.period ?? derivedCurrent);
                  const nextPeriod    = (bidInfo[gid]?.next_period    ?? derivedNext);

                  const organiserPeriod = Number(group.organiser_benefit_period);
                  const organiserIsCurrent = (currentPeriod != null) && Number(currentPeriod) === organiserPeriod;
                  const organiserIsNext    = (!isLiveNow) && (nextPeriod != null) && Number(nextPeriod) === organiserPeriod;

                  const isOwner = Number(group.owner_id) === Number(user?.id);
                  const isSuper = user?.access_level === 'superadmin';

                  return (
                    <>
                      <BidStatusBanner
                        active={isLiveNow && !organiserIsCurrent}
                        endTime={!organiserIsCurrent ? (biddingTimes[gid]?.end_time || null) : null}
                        // If organiser benefit is the current period, do NOT show a "Next bid starts in" timer.
                        // (User preference: hide next-bid timer when organiser benefit applies.)
                        nextStartMs={!isLiveNow && !organiserIsNext && !organiserIsCurrent ? (nextScheduleStarts[gid] ?? null) : null}
                        nextDateTimeLabel={!isLiveNow && !organiserIsNext && !organiserIsCurrent ? nextDateTimeLabel : ''}
                      />

                      {(organiserIsCurrent || organiserIsNext) && (
                        <div style={{ marginTop: 6 }}>
                          <span className="chip chip-muted">
                            Organiser benefit {organiserIsCurrent ? '(happening now — no bidding)' : '(no bidding)'}
                          </span>
                        </div>
                      )}

                      {organiserIsCurrent && (isOwner || isSuper) && (
                        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
                          Winner for organiser benefit is set automatically — no finalisation needed.
                        </div>
                      )}

{/* Manual open only: bidding popup opens ONLY when user taps this button */}
                      {!organiserIsCurrent && !organiserIsNext && (
                        <div style={{ marginTop: 10 }}>
                          <button
                            className="btn btn-primary"
                            type="button"
                            disabled={!isLiveNow}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveBiddingGroup(group);
                              setShowBiddingPopup(true);
                            }}
                            title={isLiveNow ? 'Open bidding' : 'Bidding is not live right now'}
                          >
                            {isLiveNow ? 'Open bidding' : 'Bidding not live'}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="group-header" onClick={() => setExpandedGroupId((prev) => (prev === gid ? null : gid))}>
                  <h4>{group.name}</h4>
                  <span className="badge">{uiStatus}</span>
                </div>

                {(() => {
                  const p = progressById?.[gid];
                  const finished = Number(p?.finished ?? 0);
                  const total = Number(p?.total ?? group.duration ?? 0);
                  const percent = Number(p?.percent ?? (total > 0 ? Math.round((finished / total) * 100) : 0));
                  return (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        Progress: {finished} / {total} periods ({percent}%)
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: 'rgba(0,0,0,0.1)', overflow: 'hidden', marginTop: 4 }}>
                        <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, percent))}%`, borderRadius: 999, background: 'rgba(0,0,0,0.35)' }} />
                      </div>
                    </div>
                  );
                })()}

                {expandedGroupId === gid && (
                  <div className="group-details">
                    <p><strong>Description:</strong> {group.description}</p>
                    <p><strong>Organiser:</strong> {organiser.name}</p>
                    <p><strong>Email:</strong> {organiser.email}</p>
                    <p><strong>Mobile:</strong> {organiser.mobile}</p>
                    <p>
                      <strong>Start Date:</strong>{' '}
                      {group.auction_start_date
                        ? DateTime.fromISO(group.auction_start_date).toFormat('dd/LL/yyyy HH:mm')
                        : '—'}
                    </p>
                    <p><strong>Duration:</strong> {durationLabel}</p>
                    <p><strong>Contribution:</strong> ${group.contribution} per {frequency}</p>
                    <p><strong>Organiser Benefit Period:</strong> {group.organiser_benefit_period}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showBiddingPopup && activeBiddingGroup && (
        (() => {
          const gid = (activeBiddingGroup?.id ?? activeBiddingGroup?.group_id);
          const windowOverride = gid ? biddingTimes[gid] : null;
          const periodNumber   = gid
            ? (
                bidInfo[gid]?.current_period ??
                bidInfo[gid]?.period_number ??
                bidStatusMap[gid]?.period_number ??
                bidStatusMap[gid]?.current_period
              )
            : undefined;
          return (
            <BiddingPopup
                group={{ ...activeBiddingGroup, group_id: activeBiddingGroup.id ?? activeBiddingGroup.group_id }}
                user={effectiveUser}
                onClose={() => {
                setShowBiddingPopup(false);
                setActiveBiddingGroup(null);
                const gid = (activeBiddingGroup?.id ?? activeBiddingGroup?.group_id);
                const status = gid ? biddingTimes[gid] : null;
                const openKey = `${gid}:${status?.period_number ?? 'na'}`;
                setPopupShownForGroup(openKey);
              }}
            windowOverride={windowOverride}
            periodNumber={periodNumber}
            statusOverride={gid ? bidStatusMap[gid] : null}
          />
          );
        })()
      )}


      {/* ===== Create Group Request ===== */}
      <div style={{ marginTop: 24 }}>
        <OrganiserCreateCard user={effectiveUser} />
      </div>

    </div>
  );
}